import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Shirt, CalendarDays, DollarSign, TrendingUp, Loader2, Download, AlertTriangle, Clock, Sparkles, Receipt, Banknote, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { SettlementAttentionCard } from "../components/admin/settlement/SettlementAttentionCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [outstanding, setOutstanding] = useState(null);

  useEffect(() => { checkAuth(); fetchStats(); fetchOutstanding(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    try { await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }); }
    catch { localStorage.removeItem("adminToken"); navigate("/admin/login"); }
  };

  const fetchStats = async () => {
    try { const response = await axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } }); setStats(response.data); }
    catch { toast.error("Error loading dashboard"); }
    finally { setLoading(false); }
  };

  const fetchOutstanding = async () => {
    try { const r = await axios.get(`${API}/admin/outstanding-balances`, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } }); setOutstanding(r.data); }
    catch { /* widget hides on error */ }
  };

  const downloadCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/ical`, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'mahra-bookings.ics');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Calendar downloaded");
    } catch { toast.error("Error downloading calendar"); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#9A7B4F]" /></div>;

  const statCards = [
    { label: "Total Pieces", value: stats?.total_outfits || 0, icon: Shirt, sub: "All-time", testid: "stat-total-pieces" },
    { label: "Products Rented", value: stats?.products_rented || 0, icon: Shirt, sub: "Active child products · All-time", testid: "stat-products-rented" },
    { label: "Bookings", value: stats?.total_bookings || 0, icon: CalendarDays, sub: `${stats?.completed_bookings || 0} completed · ${stats?.scheduled_bookings || 0} scheduled`, testid: "stat-logical-bookings" },
    { label: "Revenue", value: `$${(stats?.total_revenue_with_charges || stats?.revenue_actual || 0).toFixed(0)}`, icon: DollarSign, sub: "Paid rental revenue · All-time", testid: "stat-revenue" },
    {
      // Patch B.3 — top Net Profit card uses ALL-TIME basis so it lines up
      // with the all-time Revenue card next to it. The 30-day view lives
      // in the dedicated Profitability panel below.
      label: "Net Profit",
      value: `$${(stats?.profitability_all_time?.net_profit ?? stats?.profitability?.net_profit ?? 0).toFixed(0)}`,
      sub: stats?.profitability_all_time
        ? `${stats.profitability_all_time.profit_margin_pct.toFixed(1)}% margin · All-time`
        : (stats?.profitability
            ? `${stats.profitability.profit_margin_pct.toFixed(1)}% margin · Last 30d`
            : "—"),
      icon: TrendingUp,
      testid: "stat-net-profit",
      tone: (stats?.profitability_all_time?.net_profit ?? stats?.profitability?.net_profit ?? 0) >= 0 ? "positive" : "negative",
      link: "/admin/analytics?tab=profitability",
    },
  ];

  const chargesByType = stats?.charges_by_type || {};

  return (
    <AdminLayout
      title="Dashboard"
      subtitle="Overview of your business"
      actions={
        <Button onClick={downloadCalendar} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.15em] uppercase" data-testid="download-calendar-btn">
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />Export Calendar
        </Button>
      }
    >
      <SettlementAttentionCard />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {statCards.map((stat, i) => {
          const positive = stat.tone === "positive";
          const negative = stat.tone === "negative";
          const inner = (
            <div className={`bg-white p-6 border border-[#282828]/10 animate-fadeIn stagger-${i+1} ${stat.link ? "hover:border-[#9A7B4F]/40 transition-colors cursor-pointer" : ""}`} data-testid={stat.testid}>
              <div className="w-10 h-10 bg-[#F0D9D5]/20 flex items-center justify-center mb-4"><stat.icon className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} /></div>
              <p className={`text-2xl mb-1 ${positive ? "text-[#0E7C5A]" : negative ? "text-[#B23A48]" : "text-[#676664]"}`}>{stat.value}</p>
              <p className="text-[11px] text-[#676664]/40 font-light">{stat.label}</p>
              {stat.sub && <p className="text-[10px] text-[#676664]/40 mt-0.5">{stat.sub}</p>}
            </div>
          );
          return stat.link ? <Link key={stat.label} to={stat.link}>{inner}</Link> : <div key={stat.label}>{inner}</div>;
        })}
      </div>

      {/* ── Phase 3 — Profitability section ──────────────────────────── */}
      {stats?.profitability && (
        <div className="bg-white border border-[#282828]/10 border-l-[3px] border-l-[#0E7C5A] p-6 mb-10" data-testid="profitability-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[12px] tracking-[0.15em] uppercase text-[#676664] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} /> Profitability
              <span className="text-[10px] text-[#676664]/50 normal-case tracking-normal">Last {stats.profitability.period || "30 days"}</span>
            </h2>
            <Link to="/admin/analytics?tab=profitability" className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline">View full report →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-2 text-[13px]">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Gross Revenue</div>
              <div className="text-[#282828] text-lg" data-testid="prof-gross">${stats.profitability.gross_revenue.toFixed(2)}</div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Stripe Fees</div>
              <div className="text-[#B23A48] text-lg" data-testid="prof-fees">−${stats.profitability.stripe_fees.toFixed(2)}</div>
              {stats.profitability.stripe_fee_unknown_count > 0 && (
                <div className="text-[9px] text-[#B8761C]">{stats.profitability.stripe_fee_unknown_count} fee(s) not captured</div>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Business Costs</div>
              <div className="text-[#B23A48] text-lg" data-testid="prof-costs">−${stats.profitability.business_costs.toFixed(2)}</div>
            </div>
            <div className="space-y-1 sm:col-span-2 lg:col-span-1 lg:border-l lg:border-[#282828]/10 lg:pl-6">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Net Profit</div>
              <div className={`text-2xl ${stats.profitability.net_profit >= 0 ? "text-[#0E7C5A]" : "text-[#B23A48]"}`} data-testid="prof-net">${stats.profitability.net_profit.toFixed(2)}</div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">Profit Margin</div>
              <div className={`text-2xl ${stats.profitability.profit_margin_pct >= 0 ? "text-[#0E7C5A]" : "text-[#B23A48]"}`} data-testid="prof-margin">{stats.profitability.profit_margin_pct.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}
      {/* ── /Phase 3 ──────────────────────────────────────────────────── */}

      {/* ── Phase 4 — Outstanding Balances widget ─────────────────────── */}
      {outstanding && outstanding.count > 0 && (
        <div className="bg-white p-6 border border-l-[3px] border-l-[#B8761C] border-[#282828]/10 mb-10" data-testid="outstanding-balances-widget">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="text-sm text-[#676664] flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#B8761C]" strokeWidth={1.5} />Outstanding Balances</h3>
              <p className="text-[11px] text-[#676664]/50 mt-1">Charges raised but not yet settled.</p>
            </div>
            <div className="text-right">
              <p className="text-2xl text-[#B8761C] font-light">${outstanding.total.toFixed(2)}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#676664]/50">{outstanding.count} charge{outstanding.count === 1 ? "" : "s"} owing</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-[11px] mb-4">
            <div className="bg-amber-50 border border-amber-200 p-3">
              <div className="text-[#676664]/60 uppercase tracking-wider text-[10px]">Awaiting External Payment</div>
              <div className="text-[#B8761C] font-medium text-base">${outstanding.awaiting_external_total.toFixed(2)} <span className="text-[#676664]/50 text-[10px] font-normal">({outstanding.awaiting_external_count})</span></div>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-3">
              <div className="text-[#676664]/60 uppercase tracking-wider text-[10px]">Stripe Invoice Sent</div>
              <div className="text-[#3A5BA6] font-medium text-base">${outstanding.stripe_sent_total.toFixed(2)} <span className="text-[#676664]/50 text-[10px] font-normal">({outstanding.stripe_sent_count})</span></div>
            </div>
          </div>
          <div className="border-t border-[#282828]/5 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-2">Top items</div>
            <div className="space-y-1.5">
              {outstanding.items.slice(0, 5).map((it) => (
                <Link
                  key={it.charge_id}
                  to={`/admin/bookings?q=${encodeURIComponent(it.booking_id)}`}
                  className="flex items-center justify-between text-[12px] py-1.5 px-2 -mx-2 hover:bg-[#FAF8F0] rounded"
                  data-testid={`outstanding-item-${it.charge_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-[#9A7B4F]">{it.booking_id}</span>
                      <span className="text-[#282828]">{it.charge_type_label}</span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${it.status === "awaiting_external_payment" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                        {it.status === "awaiting_external_payment" ? "Awaiting Ext" : "Stripe Sent"}
                      </span>
                    </div>
                    <div className="text-[10px] text-[#676664]/60 truncate">
                      {it.customer_name || it.customer_email}
                      {it.due_date && <span> · Due {it.due_date}</span>}
                    </div>
                  </div>
                  <span className="text-[#B8761C] font-medium text-[13px] ml-3">${it.amount.toFixed(2)}</span>
                </Link>
              ))}
              {outstanding.count > 5 && (
                <Link to="/admin/bookings" className="block text-[11px] text-[#9A7B4F] hover:underline text-center pt-2">
                  View all {outstanding.count} outstanding charges →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white p-6 border border-[#282828]/10">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Security Bonds</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Currently Held</span><span className="text-[#676664]">${(stats?.deposits_held || 0).toFixed(2)}</span></div>
            <div className="flex justify-between items-center py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Refunded</span><span className="text-[#9A7B4F]">${(stats?.deposits_refunded || 0).toFixed(2)}</span></div>
            <div className="flex justify-between items-center py-2"><span className="text-[#676664]/50 text-sm font-light">Retained ({stats?.deposits_retained_count || 0})</span><span className="text-[#B23A48]">${(stats?.deposits_retained || 0).toFixed(2)}</span></div>
          </div>
        </div>
        <div className="bg-white p-6 border border-[#282828]/10">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><Banknote className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Charges</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Total Raised ({stats?.charges_total_raised_count || 0})</span><span className="text-[#676664]">${(stats?.charges_total_raised || 0).toFixed(2)}</span></div>
            <div className="flex justify-between items-center py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Collected</span><span className="text-[#0E7C5A]">${(stats?.charges_paid_total || 0).toFixed(2)}</span></div>
            <div className="flex justify-between items-center py-2"><span className="text-[#676664]/50 text-sm font-light">Outstanding ({stats?.charges_outstanding_count || 0})</span><span className="text-[#B8761C]">${(stats?.charges_outstanding_total || 0).toFixed(2)}</span></div>
          </div>
        </div>
        <div className="bg-white p-6 border border-[#282828]/10">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Collection Method</h3>
          <div className="space-y-4">
            {/* Patch B.2 — Rental payments (from bookings.rental_payment_method) */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-2">Rental Payments</p>
              <div className="flex justify-between items-center py-1.5 border-b border-[#282828]/5" data-testid="collection-rental-external">
                <span className="text-[#676664]/60 text-[13px] font-light">Paid Externally ({stats?.rental_paid_external_count || 0})</span>
                <span className="text-[#676664] text-[13px]">${(stats?.rental_paid_external_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5" data-testid="collection-rental-stripe">
                <span className="text-[#676664]/60 text-[13px] font-light">Paid via Stripe ({stats?.rental_paid_stripe_count || 0})</span>
                <span className="text-[#9A7B4F] text-[13px]">${(stats?.rental_paid_stripe_total || 0).toFixed(2)}</span>
              </div>
            </div>
            {/* Extra charges (damage / late / cleaning etc) */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-2">Extra Charges</p>
              <div className="flex justify-between items-center py-1.5 border-b border-[#282828]/5">
                <span className="text-[#676664]/60 text-[13px] font-light">Paid Externally ({stats?.charges_paid_external_count || 0})</span>
                <span className="text-[#676664] text-[13px]">${(stats?.charges_paid_external_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-[#282828]/5" data-testid="collection-charges-stripe-card">
                <span className="text-[#676664]/60 text-[13px] font-light">Saved Card Charged ({stats?.charges_paid_stripe_card_count || 0})</span>
                <span className="text-[#9A7B4F] text-[13px]">${(stats?.charges_paid_stripe_card_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-[#282828]/5" data-testid="collection-charges-stripe-link">
                <span className="text-[#676664]/60 text-[13px] font-light">Paid via Stripe Link ({stats?.charges_paid_stripe_link_count || 0})</span>
                <span className="text-[#9A7B4F] text-[13px]">${(stats?.charges_paid_stripe_link_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-[#282828]/5" data-testid="collection-charges-bond">
                <span className="text-[#676664]/60 text-[13px] font-light">Deducted from Bond ({stats?.charges_paid_bond_count || 0})</span>
                <span className="text-[#676664] text-[13px]">${(stats?.charges_paid_bond_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="text-[#676664]/60 text-[13px] font-light">Stripe Links Outstanding</span>
                <span className="text-[#3A5BA6] text-[13px]">${(stats?.charges_outstanding_stripe_total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {[
          { type: "damage", label: "Damage", icon: Wrench, colour: "#B23A48" },
          { type: "cleaning", label: "Cleaning", icon: Receipt, colour: "#3A5BA6" },
          { type: "late_fee", label: "Late Fees", icon: Clock, colour: "#B8761C" },
          { type: "missing", label: "Missing Items", icon: AlertTriangle, colour: "#6F3FB2" },
          { type: "other", label: "Other", icon: Receipt, colour: "#676664" },
        ].map(({ type, label, icon: Icon, colour }) => {
          const t = chargesByType[type] || { raised_count: 0, raised_total: 0, paid_count: 0, paid_total: 0, outstanding_count: 0, outstanding_total: 0 };
          return (
            <div key={type} className="bg-white p-5 border border-[#282828]/10" data-testid={`charge-type-tile-${type}`}>
              <div className="flex items-center gap-2 mb-3"><Icon className="w-3.5 h-3.5" style={{ color: colour }} strokeWidth={1.5} /><h3 className="text-[12px] text-[#676664]">{label}</h3></div>
              <p className="text-xl text-[#676664] mb-1">${t.paid_total.toFixed(0)}</p>
              <p className="text-[10px] text-[#676664]/40 uppercase tracking-wider">Collected · {t.paid_count}</p>
              {t.outstanding_total > 0 && <p className="text-[10px] mt-2" style={{ color: colour }}>+ ${t.outstanding_total.toFixed(0)} outstanding</p>}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <div className="bg-white p-6 border border-[#282828]/10">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Bookings</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Total logical</span><span className="text-[#676664]" data-testid="dashboard-total-bookings">{stats?.total_bookings || 0}</span></div>
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Completed</span><span className="text-[#9A7B4F]" data-testid="dashboard-completed-bookings">{stats?.completed_bookings || 0}</span></div>
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Scheduled</span><span className="text-[#676664]" data-testid="dashboard-scheduled-bookings">{stats?.scheduled_bookings || 0}</span></div>
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Products rented</span><span className="text-[#676664]" data-testid="dashboard-products-rented">{stats?.products_rented || 0}</span></div>
            <div className="flex justify-between items-center py-3"><span className="text-[#676664]/50 text-sm font-light">Cancelled</span><span className="text-[#676664]/60">{stats?.cancelled_bookings || 0}</span></div>
          </div>
        </div>
        <div className="bg-white p-6 border border-[#282828]/10">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Legacy Custom Charges</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Charged ({stats?.custom_charges_count || 0})</span><span className="text-[#676664]">${(stats?.custom_charges_total || 0).toFixed(2)}</span></div>
            <div className="flex justify-between items-center py-3"><span className="text-[#676664]/50 text-sm font-light">Total Collected</span><span className="text-[#9A7B4F]">${(stats?.custom_charges_total || 0).toFixed(2)}</span></div>
          </div>
        </div>
      </div>

      {/* Trial Leads & Bookings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <Link to="/admin/trial-leads" className="bg-white p-6 border border-[#282828]/10 hover:border-[#9A7B4F]/40 transition-colors block" data-testid="dashboard-trial-leads-card">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Trial Leads</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Total</span><span className="text-[#676664]" data-testid="dashboard-trial-leads-total">{stats?.trial_leads_total || 0}</span></div>
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">New</span><span className="text-[#676664]" data-testid="dashboard-trial-leads-new">{stats?.trial_leads_new || 0}</span></div>
            <div className="flex justify-between items-center py-3"><span className="text-[#676664]/50 text-sm font-light">Contacted</span><span className="text-[#9A7B4F]">{stats?.trial_leads_contacted || 0}</span></div>
          </div>
        </Link>
        <Link to="/admin/calendar" className="bg-white p-6 border border-[#282828]/10 hover:border-[#9A7B4F]/40 transition-colors block" data-testid="dashboard-trial-bookings-card">
          <h3 className="text-sm text-[#676664] mb-6 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />Trial Bookings</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-[#282828]/5"><span className="text-[#676664]/50 text-sm font-light">Scheduled (non-cancelled)</span><span className="text-[#676664]" data-testid="dashboard-trial-appointments-total">{stats?.trial_appointments_total || 0}</span></div>
            <div className="flex justify-between items-center py-3"><span className="text-[#676664]/50 text-sm font-light">Converted from leads</span><span className="text-[#9A7B4F]" data-testid="dashboard-trial-leads-converted">{stats?.trial_leads_converted || 0}</span></div>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/admin/outfits" className="block"><div className="bg-[#D4B8B4] p-8 hover:bg-[#B89A6D] transition-colors"><Shirt className="w-8 h-8 mb-4 text-white" strokeWidth={1} /><h3 className="text-xl text-white mb-2">Manage Collection</h3><p className="text-white/60 text-sm font-light">Add, edit, or remove pieces</p></div></Link>
        <Link to="/admin/bookings" className="block"><div className="bg-[#F0D9D5] p-8 hover:bg-[#DCA5A9] transition-colors"><CalendarDays className="w-8 h-8 mb-4 text-[#676664]" strokeWidth={1} /><h3 className="text-xl text-[#676664] mb-2">View Bookings</h3><p className="text-[#676664]/60 text-sm font-light">Manage reservations & security bonds</p></div></Link>
        <Link to="/admin/looks" className="block"><div className="bg-[#E8DDD0] p-8 hover:bg-[#D4C4B0] transition-colors"><Sparkles className="w-8 h-8 mb-4 text-[#9A7B4F]" strokeWidth={1} /><h3 className="text-xl text-[#9A7B4F] mb-2">Looks Manager</h3><p className="text-[#676664]/60 text-sm font-light">Curate, edit, and reorder styled looks</p></div></Link>
      </div>
    </AdminLayout>
  );
}
